import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

export type AdminMultiSelectOption = {
  value: string;
  label: string;
};

interface AdminMultiSelectProps {
  label: string;
  options: AdminMultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const AdminMultiSelect: React.FC<AdminMultiSelectProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputId = useId();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [options, query]);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? selected[0]
        : `${selected.length} selected`;

  return (
    <div className={`min-w-[200px] ${className}`.trim()} ref={rootRef}>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <button
          id={inputId}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 p-2 text-left text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:bg-gray-50 disabled:opacity-60"
        >
          <span className={selected.length ? 'text-gray-900 truncate' : 'text-gray-500 truncate'}>
            {summary}
          </span>
          <span className="text-gray-400 shrink-0">{open ? '▴' : '▾'}</span>
        </button>

        {open && !disabled && (
          <div className="absolute z-30 mt-1 w-full min-w-[220px] rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                className="w-full p-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Filter…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
              ) : (
                filtered.map((opt) => {
                  const checked = selected.includes(opt.value);
                  return (
                    <li key={opt.value}>
                      <label className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          checked={checked}
                          onChange={() => toggle(opt.value)}
                        />
                        <span className="truncate text-gray-800">{opt.label}</span>
                      </label>
                    </li>
                  );
                })
              )}
            </ul>
            {selected.length > 0 && (
              <div className="border-t border-gray-100 px-3 py-1.5 flex justify-end">
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => onChange([])}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMultiSelect;
